import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from '@itsrever/design-system'
import { GroupSettingsApi } from '@/infrastructure/api/returns-and-refunds'
import { LayeredActions } from '@/domain/group-settings'
import {
  captureReturnsAndRefundsEvent,
  ReturnsAndRefundsEvent
} from '@/infrastructure/posthog'
import { rejection } from './rejection'

export interface GroupSettingsMessages {
  saveSuccess: string
  saveError: string
  resetSuccess: string
  resetError: string
}

export interface GroupSettingsTracking {
  // Sent with every save and reset, e.g. which setting and whether REVER made it.
  properties: Record<string, unknown>
  // The group's portals, to count how many a group save reaches.
  stores: string[]
}

// Reads one kind of group settings and binds the calls that change it, all invalidating the one group read.
export function useGroupSettings<T>(
  name: string,
  api: GroupSettingsApi<T>,
  group: string | undefined,
  messages: GroupSettingsMessages,
  tracking?: GroupSettingsTracking
) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const queryKey = ['groupSettings', name, group]
  const query = useQuery({
    queryKey,
    queryFn: ({ signal }) => api.find(group ?? '', signal),
    enabled: !!group
  })
  const mutation = <Input>(
    mutationFn: (input: Input) => Promise<void>,
    success: string,
    error: string,
    track: {
      event: ReturnsAndRefundsEvent
      properties: (input: Input) => Record<string, unknown>
    }
  ) => {
    const capture = (input: Input, succeeded: boolean) => {
      if (!tracking || !group) return
      captureReturnsAndRefundsEvent(track.event, {
        group,
        ...tracking.properties,
        ...track.properties(input),
        succeeded
      })
    }
    return {
      mutationFn,
      onSuccess: (_: void, input: Input) => {
        capture(input, true)
        toast({ text: t(success), variant: 'success' })
        queryClient.invalidateQueries({ queryKey })
      },
      onError: (e: unknown, input: Input) => {
        capture(input, false)
        console.warn(e)
        toast({ text: rejection(e) ?? t(error), variant: 'error' })
      }
    }
  }
  // A group save reaches the portals that follow the group, not those with their own settings.
  const storesFollowingGroup = () =>
    (tracking?.stores ?? []).filter((store) => !query.data?.overrides[store])
      .length
  const saveGroup = useMutation(
    mutation(
      (value: T) => api.saveGroup(group ?? '', value),
      messages.saveSuccess,
      messages.saveError,
      {
        event: 'Returns and refunds settings saved',
        properties: () => ({
          level: 'group',
          storesUpdated: storesFollowingGroup()
        })
      }
    )
  )
  const saveStore = useMutation(
    mutation(
      ({ ecommerceId, value }: { ecommerceId: string; value: T }) =>
        api.saveStore(ecommerceId, value),
      messages.saveSuccess,
      messages.saveError,
      {
        event: 'Returns and refunds settings saved',
        properties: ({ ecommerceId }) => ({
          level: 'store',
          ecommerceId,
          storesUpdated: 1
        })
      }
    )
  )
  const resetStore = useMutation(
    mutation(
      (ecommerceId: string) => api.resetStore(ecommerceId),
      messages.resetSuccess,
      messages.resetError,
      {
        event: 'Returns and refunds store settings reset',
        properties: (ecommerceId) => ({ ecommerceId })
      }
    )
  )
  const actions: LayeredActions<T> = {
    saveGroup: (value) => saveGroup.mutate(value),
    savingGroup: saveGroup.isPending,
    saveStore: (ecommerceId, value) => saveStore.mutate({ ecommerceId, value }),
    savingStore: saveStore.isPending,
    resetStore: (ecommerceId, options) =>
      resetStore.mutate(ecommerceId, options),
    resettingStore: resetStore.isPending
  }
  return {
    layered: query.data,
    loading: query.isLoading,
    failed: query.isError,
    reload: query.refetch,
    actions
  }
}
